import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Stepper } from "@/components/wizard/Stepper";
import { ConsentStep } from "@/components/wizard/steps/ConsentStep";
import { PersonalStep } from "@/components/wizard/steps/PersonalStep";
import { AddressStep } from "@/components/wizard/steps/AddressStep";
import { FormSelectionStep } from "@/components/wizard/steps/FormSelectionStep";
import { FormSpecificStep } from "@/components/wizard/steps/FormSpecificStep";
import { ReviewStep } from "@/components/wizard/steps/ReviewStep";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, FileDown, Save } from "lucide-react";

const DRAFT_KEY = "immiform-draft-v1";

const emptyState = {
  consent: { acknowledged: false, signature: "" },
  personal: {
    family_name: "",
    given_name: "",
    middle_name: "",
    date_of_birth: "",
    gender: "",
    social_security_number: "",
    alien_registration_number: "",
    email: "",
    phone: "",
  },
  address: {
    street_address: "",
    apartment: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
  },
  form_code: "",
  form_specific: {},
};

export default function WizardPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { formId: editId } = useParams();
  const isEditing = Boolean(editId);

  const [current, setCurrent] = useState(0);
  const [data, setData] = useState(emptyState);
  const [catalog, setCatalog] = useState([]);
  const [definitions, setDefinitions] = useState({});
  const [validation, setValidation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [autosavedAt, setAutosavedAt] = useState(null);
  const loaded = useRef(false);

  // Load catalog
  useEffect(() => {
    api
      .get("/forms/catalog")
      .then((res) => setCatalog(res.data.forms || []))
      .catch(() => toast.error("Failed to load form catalog"));
  }, []);

  // Load either an existing form (edit) or a saved draft
  useEffect(() => {
    if (loaded.current) return;
    if (isEditing) {
      loaded.current = true;
      api
        .get(`/forms/${editId}`)
        .then((res) => {
          const f = res.data.form;
          const d = f.data || {};
          setData({
            consent: {
              acknowledged: !!d.consent?.acknowledged,
              signature: d.consent?.signature || "",
            },
            personal: {
              family_name: d.personal_information?.family_name || "",
              given_name: d.personal_information?.given_name || "",
              middle_name: d.personal_information?.middle_name || "",
              date_of_birth: d.personal_information?.date_of_birth || "",
              gender: d.personal_information?.gender || "",
              // SSN comes masked from the API — clear it and let user re-enter
              // if they want to change it. Empty = keep existing on save.
              social_security_number: "",
              alien_registration_number:
                d.personal_information?.alien_registration_number || "",
              email: d.personal_information?.email || "",
              phone: d.personal_information?.phone || "",
            },
            address: {
              street_address: d.address_information?.street_address || "",
              apartment: d.address_information?.apartment || "",
              city: d.address_information?.city || "",
              state: d.address_information?.state || "",
              zip_code: d.address_information?.zip_code || "",
              country: d.address_information?.country || "",
            },
            form_code: f.form_code || "",
            form_specific: d.form_specific || {},
            _existing_ssn_mask: d.personal_information?.social_security_number || "",
          });
        })
        .catch(() => toast.error("Failed to load form"));
      return;
    }
    // New wizard — try to restore local draft
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setData((prev) => ({ ...prev, ...parsed }));
          toast.success(t.common.draft_restored);
        }
      }
    } catch (_) {
      // ignore
    }
    loaded.current = true;
  }, [editId, isEditing, t.common.draft_restored]);

  // Load selected form definition
  useEffect(() => {
    if (data.form_code && !definitions[data.form_code]) {
      api
        .get(`/forms/catalog/${data.form_code}`)
        .then((res) =>
          setDefinitions((prev) => ({ ...prev, [data.form_code]: res.data })),
        )
        .catch(() => toast.error("Failed to load form definition"));
    }
  }, [data.form_code, definitions]);

  // Auto-save draft locally (new wizard only). SSN is stripped for safety.
  useEffect(() => {
    if (!loaded.current || isEditing) return;
    const id = setTimeout(() => {
      try {
        const safe = {
          ...data,
          personal: { ...data.personal, social_security_number: "" },
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(safe));
        setAutosavedAt(new Date());
      } catch (_) {
        /* ignore quota */
      }
    }, 400);
    return () => clearTimeout(id);
  }, [data, isEditing]);

  const steps = useMemo(
    () => [
      { id: "consent", title: t.wizard.step_consent },
      { id: "personal", title: t.wizard.step_personal },
      { id: "address", title: t.wizard.step_address },
      { id: "form", title: t.wizard.step_form },
      { id: "specific", title: t.wizard.step_specific },
      { id: "review", title: t.wizard.step_review },
    ],
    [t],
  );

  const validateCurrent = () => {
    const e = {};
    if (current === 0) {
      if (!data.consent.acknowledged) e.acknowledged = t.common.required;
      if (!data.consent.signature?.trim()) e.signature = t.common.required;
      setErrors({ consent: e });
      return Object.keys(e).length === 0;
    }
    if (current === 1) {
      if (!data.personal.family_name?.trim()) e.family_name = t.common.required;
      if (!data.personal.given_name?.trim()) e.given_name = t.common.required;
      if (!data.personal.date_of_birth) e.date_of_birth = t.common.required;
      setErrors({ personal: e });
      return Object.keys(e).length === 0;
    }
    if (current === 2) {
      ["street_address", "city", "state", "zip_code", "country"].forEach((k) => {
        if (!data.address[k]?.trim()) e[k] = t.common.required;
      });
      setErrors({ address: e });
      return Object.keys(e).length === 0;
    }
    if (current === 3) {
      if (!data.form_code) {
        toast.error(t.common.required);
        return false;
      }
      return true;
    }
    return true;
  };

  const goNext = async () => {
    if (!validateCurrent()) return;
    setCurrent((c) => Math.min(c + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (current + 1 === steps.length - 1) {
      await runPreviewValidation();
    }
  };

  const goBack = () => {
    setCurrent((c) => Math.max(c - 1, 0));
    setErrors({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const buildPayload = () => ({
    form_code: data.form_code,
    consent: {
      acknowledged: data.consent.acknowledged,
      signature: data.consent.signature,
    },
    personal_information: {
      family_name: data.personal.family_name,
      given_name: data.personal.given_name,
      middle_name: data.personal.middle_name,
      date_of_birth: data.personal.date_of_birth,
      gender: data.personal.gender,
      // On edit with an unchanged SSN field, send empty — backend preserves ciphertext
      social_security_number: data.personal.social_security_number,
      alien_registration_number: data.personal.alien_registration_number,
      email: data.personal.email,
      phone: data.personal.phone,
    },
    address_information: { ...data.address },
    form_specific: data.form_specific || {},
  });

  const runPreviewValidation = async () => {
    try {
      const res = await api.post("/forms/preview-validate", buildPayload());
      setValidation(res.data.validation);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Validation failed");
    }
  };

  const handleGenerate = async () => {
    setSubmitting(true);
    try {
      let formId = editId;
      if (isEditing) {
        const updated = await api.put(`/forms/${editId}`, buildPayload());
        setValidation(updated.data.validation);
      } else {
        const created = await api.post("/forms", buildPayload());
        formId = created.data.form.id;
        setValidation(created.data.validation);
        // Clear the local draft — this is now persisted server-side
        try {
          localStorage.removeItem(DRAFT_KEY);
        } catch (_) {
          /* ignore */
        }
      }
      const pdf = await api.get(`/forms/${formId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([pdf.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `USCIS_${data.form_code}_${formId.slice(0, 8)}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t.success.title);
      navigate(`/success/${formId}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to generate PDF");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDiscardDraft = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (_) {
      /* ignore */
    }
    setData(emptyState);
    setCurrent(0);
    setErrors({});
    setValidation(null);
    toast.success(t.common.discard_draft);
  };

  const currentDefinition = definitions[data.form_code];

  const summary = useMemo(() => {
    const ssnDisplay = data.personal.social_security_number
      ? "•••••" + (data.personal.social_security_number.slice(-4) || "")
      : data._existing_ssn_mask || "";
    return [
      { label: "Signature", value: data.consent.signature },
      {
        label: "Name",
        value: [
          data.personal.given_name,
          data.personal.middle_name,
          data.personal.family_name,
        ]
          .filter(Boolean)
          .join(" "),
      },
      { label: "DOB", value: data.personal.date_of_birth },
      { label: "Gender", value: data.personal.gender },
      { label: "SSN", value: ssnDisplay },
      { label: "A-Number", value: data.personal.alien_registration_number },
      {
        label: "Address",
        value: [
          data.address.street_address,
          data.address.apartment,
          data.address.city,
          data.address.state,
          data.address.zip_code,
          data.address.country,
        ]
          .filter(Boolean)
          .join(", "),
      },
      { label: "Form", value: data.form_code },
      ...Object.entries(data.form_specific || {}).map(([k, v]) => ({
        label: k,
        value: typeof v === "boolean" ? (v ? "Yes" : "No") : v,
      })),
    ];
  }, [data]);

  const personalStepData = useMemo(
    () => ({
      ...data.personal,
      ...(isEditing && data._existing_ssn_mask
        ? { _ssn_placeholder: t.common.edit_ssn_hint }
        : {}),
    }),
    [data.personal, data._existing_ssn_mask, isEditing, t.common.edit_ssn_hint],
  );

  return (
    <div className="mx-auto max-w-4xl px-6 py-10" data-testid="wizard-page">
      {/* Header line */}
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
            {isEditing ? `Edit · ${editId?.slice(0, 8)}` : "New Form"}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {autosavedAt && !isEditing && (
            <span
              data-testid="wizard-autosaved-indicator"
              className="hidden items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground sm:inline-flex"
            >
              <Save className="h-3 w-3" />
              {t.common.autosaved}
            </span>
          )}
          {!isEditing && (
            <button
              data-testid="wizard-discard-draft"
              onClick={handleDiscardDraft}
              className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground underline-offset-4 hover:text-destructive hover:underline"
            >
              {t.common.discard_draft}
            </button>
          )}
        </div>
      </div>

      {/* Stepper */}
      <div className="mb-10">
        <Stepper steps={steps} current={current} />
      </div>

      <div className="hairline bg-card p-8 md:p-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.25 }}
          >
            {current === 0 && (
              <ConsentStep
                value={data.consent}
                onChange={(v) => setData({ ...data, consent: v })}
                errors={errors.consent}
              />
            )}
            {current === 1 && (
              <PersonalStep
                value={personalStepData}
                onChange={(v) => setData({ ...data, personal: v })}
                errors={errors.personal}
              />
            )}
            {current === 2 && (
              <AddressStep
                value={data.address}
                onChange={(v) => setData({ ...data, address: v })}
                errors={errors.address}
              />
            )}
            {current === 3 && (
              <FormSelectionStep
                value={data.form_code}
                onChange={(code) =>
                  setData({ ...data, form_code: code, form_specific: {} })
                }
                catalog={catalog}
              />
            )}
            {current === 4 && (
              <FormSpecificStep
                formDefinition={currentDefinition}
                value={data.form_specific}
                onChange={(v) => setData({ ...data, form_specific: v })}
              />
            )}
            {current === 5 && (
              <ReviewStep validation={validation} summary={summary} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Action bar */}
      <div className="mt-6 flex items-center justify-between">
        <Button
          variant="outline"
          onClick={goBack}
          disabled={current === 0}
          data-testid="wizard-back-button"
          className="h-11 rounded-none border-foreground/40"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t.common.back}
        </Button>
        {current < steps.length - 1 ? (
          <Button
            onClick={goNext}
            data-testid="wizard-next-button"
            className="h-11 rounded-none bg-[hsl(var(--primary))] px-6 hover:bg-[hsl(var(--primary))]/90"
          >
            {t.common.next}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={handleGenerate}
            disabled={submitting}
            data-testid="wizard-generate-button"
            className="h-11 rounded-none bg-[hsl(var(--primary))] px-6 hover:bg-[hsl(var(--primary))]/90"
          >
            <FileDown className="mr-2 h-4 w-4" />
            {submitting ? t.review.generating : t.review.generate}
          </Button>
        )}
      </div>
    </div>
  );
}
